/* eslint-disable @nx/enforce-module-boundaries */
import React, { useState, useRef, useEffect, useMemo } from 'react';
import IconButton from '@mui/material/IconButton';
import { GoogleMap } from '@react-google-maps/api';
import * as XLSX from 'xlsx';
import expand from '../../../../assets/icons/Expand.svg';
import mapOptions from '../../../../assets/icons/Map Options.svg';
import plus from '../../../../assets/icons/Plus Stroke.svg';
import minus from '../../../../assets/icons/Minus Stroke.svg';
import locationIcon from '../../../../assets/icons/Location.svg';
import live from '../../../../assets/icons/Live.svg';
import phone from '../../../../assets/icons/phone-calling.svg';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import download from '../../../../assets/icons/download-04.svg';
import vehicleIcon from '../../../../assets/icons/Truck top view 2.svg';
import movingIcon from 'packages/novous_web/src/assets/icons/moving_green.svg';
import idleIcon from 'packages/novous_web/src/assets/icons/idle_gray.svg';
import stopIcon from 'packages/novous_web/src/assets/icons/red_dot.svg';

import movingVehicleIcon from 'packages/novous_web/src/assets/icons/MovingTruck.svg';
import idleVehicleIcon from 'packages/novous_web/src/assets/icons/IdleTruck.svg';
import stoppedVehicleIcon from 'packages/novous_web/src/assets/icons/StoppedTruck.svg';
import greenCar from 'packages/novous_web/src/assets/icons/Green_Car.png';
import yellowCar from 'packages/novous_web/src/assets/icons/Yellow_Car.png';
import redCar from 'packages/novous_web/src/assets/icons/Red_Car.png';

import greenBus from 'packages/novous_web/src/assets/icons/Green_Bus.png';
import yellowBus from 'packages/novous_web/src/assets/icons/Yellow_Bus.png';
import redBus from 'packages/novous_web/src/assets/icons/Red_Bus.png';

import greenMinivan from 'packages/novous_web/src/assets/icons/Green_Minivan.png';
import yellowMinivan from 'packages/novous_web/src/assets/icons/Yellow_Minivan.png';
import redMinivan from 'packages/novous_web/src/assets/icons/Red_Minivan.png';

import { useGoogleMaps } from 'packages/novous_web/src/context/GoogleMapContext';
import LiveFeedModal from '../../../../pages/Dashboard/Reports/components/LiveFeed';
import CallingModal from '../../../../common/components/CallingModal';
import car from 'packages/novous_web/src/assets/images/car.svg';
import truck from 'packages/novous_web/src/assets/images/truck.svg';
import minivan from 'packages/novous_web/src/assets/images/minivan.svg';
import bus from 'packages/novous_web/src/assets/images/bus.svg';
import apiClient from '../../../../api/apiClient';
import { API_ENDPOINTS } from '../../../../api/endpoints';

const containerStyle = {
  width: '100%',
  height: '100%',
};

interface MapData {
  deviceId?: string;
  vehicleRegistrationNo: string;
  lastUpdateTime: string;
  lastLatitude: string;
  lastLongitude: string;
  vehicleState: string;
  gpsSpeed: number;
  vehicleType: string;
  deviceType: string;
}

interface GeocodedVehicle {
  vehicleRegistrationNo: string;
  lastUpdateTime: string;
  address: string;
  position: { lat: number; lng: number };
  vehicleState: string;
  gpsSpeed: number;
  vehicleType: string;
  deviceType: string;
}

interface MapPreviewProps {
  width?: string;
  mapData: MapData[];
  height?: string;
  showStop?: boolean;
  zoomRatio?: number;
  focusLocation?: {
    lat: number;
    lng: number;
  };
}

type MapType = 'roadmap' | 'satellite' | 'hybrid';
type VehicleState = 'RUNNING' | 'IDLE' | 'STOP';

const formatDateTime = (dateStr: string): string => {
  const datePart = dateStr?.slice(0, 10);
  const timePart = dateStr?.slice(11);
  const dateObj = new Date(`${datePart}T${timePart}`);

  const day = dateObj.getDate().toString().padStart(2, '0');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = monthNames[dateObj.getMonth()];
  const year = dateObj.getFullYear();

  let hours = dateObj.getHours();
  const minutes = dateObj.getMinutes().toString().padStart(2, '0');
  const seconds = dateObj.getSeconds().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;
  const formattedHours = hours.toString().padStart(2, '0');

  return `${day} ${month}, ${year}, ${formattedHours}:${minutes}:${seconds} ${ampm}`;
};

const convertRegistrationNoToVehicleId = (regNo: string | undefined): string | undefined => {
  if (!regNo) return undefined;
  return regNo;
};

// Function to fetch address from backend API
const fetchAddressFromAPI = async (latitude: number, longitude: number): Promise<string> => {
  try {
    const response = await apiClient.post(`${API_ENDPOINTS.google.GOOGLE_ADDRESS}`, {
      latitude,
      longitude,
    });
    const result = response.data;
    console.info('result', result);
    if (result?.status === 200 && typeof result?.data === 'string') return result.data;
    return 'Address not found';
  } catch (error) {
    console.error('Error fetching address from API:', error);
    return 'Address not found';
  }
};

const getVehicleIcon = (vehicleType: string | null, vehicleState: VehicleState): string => {
  const normalizedVehicleType = vehicleType?.toLowerCase() || 'truck';
  let mappedVehicleType: string;
  switch (normalizedVehicleType) {
    case 'cab':
      mappedVehicleType = 'car';
      break;
    case 'truck':
      mappedVehicleType = 'truck';
      break;
    case 'bus':
      mappedVehicleType = 'bus';
      break;
    case 'minbus':
      mappedVehicleType = 'minivan';
      break;
    default:
      mappedVehicleType = 'truck';
  }

  // Handle STOP state first to ensure it doesn't fall through to default
  if (vehicleState === 'STOP') {
    switch (mappedVehicleType) {
      case 'car':
        return redCar;
      case 'bus':
        return redBus;
      case 'minivan':
        return redMinivan;
      default:
        return stoppedVehicleIcon;
    }
  }

  if (mappedVehicleType === 'truck') {
    switch (vehicleState) {
      case 'RUNNING':
        return movingVehicleIcon;
      case 'IDLE':
        return idleVehicleIcon;
      default:
        return idleVehicleIcon;
    }
  }

  switch (vehicleState) {
    case 'RUNNING':
      switch (mappedVehicleType) {
        case 'car':
          return greenCar;
        case 'bus':
          return greenBus;
        case 'minivan':
          return greenMinivan;
        default:
          return movingVehicleIcon;
      }
    case 'IDLE':
      switch (mappedVehicleType) {
        case 'car':
          return yellowCar;
        case 'bus':
          return yellowBus;
        case 'minivan':
          return yellowMinivan;
        default:
          return idleVehicleIcon;
      }
    default:
      return idleVehicleIcon;
  }
};

const MapPreview: React.FC<MapPreviewProps> = ({
  width = '100%',
  mapData,
  height = 'full',
  showStop = false,
  zoomRatio = 4,
  focusLocation,
}) => {
  console.info(mapData, 'mapData');
  const { isLoaded } = useGoogleMaps();
  const heightClass = height === 'full' ? `h-full` : `${height}`;
  const [center, setCenter] = useState<any>();
  const mapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
  const [zoom, setZoom] = useState(zoomRatio || 4);
  const clustererRef = useRef<MarkerClusterer | null>(null);
  const [mapType, setMapType] = useState<MapType>('roadmap');
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [hoveredVehicle, setHoveredVehicle] = useState<GeocodedVehicle | null>(null);
  const [selectedVehicle, setSelectedVehicle] = useState<GeocodedVehicle | null>(null);
  const [compositeIcons, setCompositeIcons] = useState<{ [key: string]: string }>({});
  const [liveFeedModalOpen, setLiveFeedModalOpen] = useState(false);
  const [callingModalOpen, setCallingModalOpen] = useState(false);
  const hideTimerRef = useRef<number | null>(null);

  // Address cache: regNo -> address
  const [vehicleAddresses, setVehicleAddresses] = useState<{ [regNo: string]: string }>({});

  // Function to fit map bounds to show all markers
  // Function to fit map bounds to show all markers
  const fitMapToMarkers = () => {
    if (!mapRef.current || !mapData || mapData.length === 0) return;

    const bounds = new window.google.maps.LatLngBounds();
    let validLocations = 0;

    mapData.forEach((vehicle) => {
      if (vehicle.lastLatitude && vehicle.lastLongitude) {
        const lat = parseFloat(vehicle.lastLatitude);
        const lng = parseFloat(vehicle.lastLongitude);
        if ((lat < -3 || lat > 3) && (lng < -3 || lng > 3)) {
          bounds.extend(new window.google.maps.LatLng(lat, lng));
          validLocations++;
        }
      }
    });

    // If we have valid bounds
    if (!bounds.isEmpty()) {
      // Calculate bounds area to check if it's too small
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      const latDiff = Math.abs(ne.lat() - sw.lat());
      const lngDiff = Math.abs(ne.lng() - sw.lng());

      // If only one location or bounds are too small, set a reasonable zoom level
      if (validLocations === 1 || latDiff < 0.01 || lngDiff < 0.01) {
        mapRef.current.setCenter(bounds.getCenter());
        mapRef.current.setZoom(12); // Reasonable zoom level for single location
      } else {
        // Multiple locations - fit to bounds with padding
        mapRef.current.fitBounds(bounds);
        const padding = 50; // pixels
        mapRef.current.panToBounds(bounds, padding);
      }
    }
  };

  useEffect(() => {
    if (mapData && mapData.length > 0) {
      const validVehicle = mapData.filter(
        (v) => {
          if (!v.lastLatitude || !v.lastLongitude) return false;

          const lat = Number(v.lastLatitude);
          const lng = Number(v.lastLongitude);
          if ((lat < -3 || lat > 3) && (lng < -3 || lng > 3)) return false;

          return true;
        }
      );
      if (validVehicle.length > 0) {
        const totalLat = validVehicle.reduce((sum, vehicle) => sum + parseFloat(vehicle.lastLatitude), 0);
        const totalLng = validVehicle.reduce((sum, vehicle) => sum + parseFloat(vehicle.lastLongitude), 0);

        setCenter({
          lat: totalLat / validVehicle.length,
          lng: totalLng / validVehicle.length,
        });
      }
    }
  }, [mapData]);

  // Fit map to markers when map is loaded and data is available
  useEffect(() => {
    if (mapRef.current && isLoaded && mapData && mapData.length > 0) {
      // Small delay to ensure map is fully rendered
      const timer = setTimeout(() => {
        fitMapToMarkers();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [mapRef.current, isLoaded, mapData]);

  useEffect(() => {
    if (mapRef.current && focusLocation) {
      mapRef.current.panTo(focusLocation);
      mapRef.current.setZoom(20);
    }
  }, [focusLocation]);

  const clearHideTimer = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  // Only fetch address on hover
  const handleMouseEnter = (vehicle?: GeocodedVehicle) => {
    clearHideTimer();
    if (vehicle) {
      setHoveredVehicle(vehicle);

      console.info('Hovered vehicle address:', vehicleAddresses[vehicle.vehicleRegistrationNo], {
        vehicleAddresses,
      });

      // If address already fetched and valid, do nothing
      const cachedAddress = vehicleAddresses[vehicle.vehicleRegistrationNo];
      if (cachedAddress && cachedAddress !== 'Address not found') return;

      setVehicleAddresses((prev) => ({
        ...prev,
        [vehicle.vehicleRegistrationNo]: 'Fetching address...',
      }));

      fetchAddressFromAPI(vehicle.position.lat, vehicle.position.lng)
        .then((address) => {
          setVehicleAddresses((prev) => ({
            ...prev,
            [vehicle.vehicleRegistrationNo]: address,
          }));

          console.info('Fetched address on hover:', address);
        })
        .catch((error) => {
          console.error('Error fetching address:', error);
          setVehicleAddresses((prev) => ({
            ...prev,
            [vehicle.vehicleRegistrationNo]: 'Address not found',
          }));
        });
    }
  };

  const handleMouseLeave = () => {
    if (liveFeedModalOpen || callingModalOpen) return;
    hideTimerRef.current = window.setTimeout(() => {
      setHoveredVehicle(null);
    }, 300);
  };

  const zoomIn = () => {
    if (mapRef.current && window.google) {
      const currentZoom = mapRef.current.getZoom() ?? 12;
      mapRef.current.setZoom(currentZoom + 1);
    }
  };

  const zoomOut = () => {
    if (mapRef.current && window.google) {
      const currentZoom = mapRef.current.getZoom() ?? 12;
      mapRef.current.setZoom(currentZoom - 1);
    }
  };

  const handleMapOptionsClick = () => {
    const newMapType: MapType = mapType === 'roadmap' ? 'hybrid' : 'roadmap';
    setMapType(newMapType);
    if (mapRef.current && window.google) {
      mapRef.current.setMapTypeId(newMapType as google.maps.MapTypeId);
    }
  };

  const handleExpand = () => {
    const element = document.getElementById('map-preview-container');
    if (!document.fullscreenElement) {
      element?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  const getTimeDifference = (lastUpdateTime: string): string => {
    const datePart = lastUpdateTime?.slice(0, 10);
    const timePart = lastUpdateTime?.slice(11);
    const lastUpdateDate = new Date(`${datePart}T${timePart}`);
    const diff = new Date().getTime() - lastUpdateDate.getTime();
    const seconds = Math.floor(diff / 1000);
    if (seconds < 60) return `${seconds} sec ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours} hr ago`;
  };

  // Download hovered vehicle details as Excel
  const handleDownload = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!hoveredVehicle) return;
    const data = [
      ['Field', 'Value'],
      ['Registration No.', hoveredVehicle.vehicleRegistrationNo],
      ['Last Update Time', formatDateTime(hoveredVehicle.lastUpdateTime)],
      ['Driver', '<N/A>'],
      ['Address', vehicleAddresses[hoveredVehicle.vehicleRegistrationNo] || 'Fetching address...'],
      ['Last Update', getTimeDifference(hoveredVehicle.lastUpdateTime)],
      ['Last Speed', hoveredVehicle.gpsSpeed],
    ];
    const worksheet = XLSX.utils.aoa_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Details');
    XLSX.writeFile(workbook, `${hoveredVehicle.vehicleRegistrationNo}_details.xlsx`);
  };

  const handleLiveClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    clearHideTimer();
    setSelectedVehicle(hoveredVehicle);
    setLiveFeedModalOpen(true);
  };

  const handlePhoneClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    clearHideTimer();
    setSelectedVehicle(hoveredVehicle);
    setCallingModalOpen(true);
  };

  // Prepare locations for markers
  const locations = useMemo(() => {
    if (!mapData || mapData.length === 0) {
      return [];
    }
    return (mapData || []).map((item) => ({
      lat: parseFloat(item.lastLatitude),
      lng: parseFloat(item.lastLongitude),
    }));
  }, [mapData]);

  // Add this function before your useEffect
  const createMarkers = () => {
    if (!mapRef.current || locations.length === 0 || !window.google) {
      console.info('❌ Cannot create markers - missing requirements');
      return;
    }

    console.info('🔄 Creating markers for', locations.length, 'locations');

    // Clear previous markers
    markersRef.current.forEach((marker) => {
      marker.map = null;
    });
    markersRef.current = [];

    // Create new markers only if we have data
    const markers: google.maps.marker.AdvancedMarkerElement[] = locations.map((location, index) => {
      const item = mapData[index];
      const state = (item.vehicleState as VehicleState) || 'IDLE';
      const vehicleMarkerIcon = getVehicleIcon(item.vehicleType, state);

      // Create a content element for AdvancedMarkerElement
      const img = document.createElement('img');
      img.src = vehicleMarkerIcon;
      img.alt = 'vehicle-marker';
      img.style.width = '20px';
      img.style.height = '40px';

      const marker = new window.google.maps.marker.AdvancedMarkerElement({
        position: location,
        content: img,
        map: mapRef.current, // Explicitly set map here
      });

      img.addEventListener('mouseover', () =>
        handleMouseEnter({
          vehicleRegistrationNo: item.vehicleRegistrationNo,
          lastUpdateTime: item.lastUpdateTime,
          address: vehicleAddresses[item.vehicleRegistrationNo] || 'Fetching address...',
          position: location,
          vehicleState: item.vehicleState,
          gpsSpeed: item.gpsSpeed,
          vehicleType: item.vehicleType,
          deviceType: item.deviceType,
        }),
      );
      img.addEventListener('mouseout', handleMouseLeave);

      return marker;
    });

    if (clustererRef.current) {
      clustererRef.current.clearMarkers();
    }
    clustererRef.current = new MarkerClusterer({
      map: mapRef.current,
      markers
    });
    markersRef.current = markers;

    console.info('✅ Markers created successfully');
  };

  useEffect(() => {
    // Don't create markers if no locations or map not loaded
    if (!mapRef.current || locations.length === 0 || !window.google) {
      // Clear existing markers
      markersRef.current.forEach((marker) => {
        marker.map = null;
      });
      markersRef.current = [];

      // Clear clusterer
      if (clustererRef.current) {
        clustererRef.current.clearMarkers();
      }
      return;
    }

    // Clear previous markers
    markersRef.current.forEach((marker) => {
      marker.map = null;
    });
    markersRef.current = [];

    // Create new markers only if we have data
    const markers: google.maps.marker.AdvancedMarkerElement[] = locations.map((location, index) => {
      const item = mapData[index];
      const state = (item.vehicleState as VehicleState) || 'IDLE';
      const vehicleMarkerIcon = getVehicleIcon(item.vehicleType, state);

      // Create a content element for AdvancedMarkerElement
      const img = document.createElement('img');
      img.src = vehicleMarkerIcon;
      img.alt = 'vehicle-marker';
      img.style.width = '20px';
      img.style.height = '40px';

      const marker = new window.google.maps.marker.AdvancedMarkerElement({
        position: location,
        content: img,
      });

      img.addEventListener('mouseover', () =>
        handleMouseEnter({
          vehicleRegistrationNo: item.vehicleRegistrationNo,
          lastUpdateTime: item.lastUpdateTime,
          address: vehicleAddresses[item.vehicleRegistrationNo] || 'Fetching address...',
          position: location,
          vehicleState: item.vehicleState,
          gpsSpeed: item.gpsSpeed,
          vehicleType: item.vehicleType,
          deviceType: item.deviceType,
        }),
      );
      img.addEventListener('mouseout', handleMouseLeave);

      return marker;
    });

    if (clustererRef.current) {
      clustererRef.current.clearMarkers();
    }
    clustererRef.current = new MarkerClusterer({ map: mapRef.current, markers });
    markersRef.current = markers;

    // Fit map to markers after creating them
    fitMapToMarkers();
  }, [locations, showStop, compositeIcons, mapData, isLoaded]);

  if (!isLoaded) return <div>Loading Map...</div>;

  return (
    <div
      id="map-preview-container"
      className={`relative ${heightClass} rounded-[12px] border border-[#dadee5] overflow-hidden`}
      style={{ width }}
    >
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={center}
        zoom={zoom}
        options={{
          zoomControl: false, // Hide zoom controls
          mapTypeControl: false, // Hide map type controls
          fullscreenControl: false, // Hide fullscreen control
          streetViewControl: false, // Hide street view control
          keyboardShortcuts: false, // Disable keyboard shortcuts
          mapTypeId: mapType,
          mapId: `novus-omnifleet-webApp`,
        }}
        onLoad={(mapInstance) => {
          mapRef.current = mapInstance;
          setMap(mapInstance);
          // Fit map to markers when map is loaded
          if (mapData && mapData.length > 0) {
            setTimeout(() => {
              createMarkers();
              fitMapToMarkers();
            }, 100);
          }
        }}
      />

      {/* Remove the top controls to make more space for the map */}
      <div className="absolute top-4 right-4 flex gap-2">
        <IconButton
          className="bg-[#fcfcfc] border border-[#dadee5] rounded-[8px] p-2 w-10 h-10"
          onClick={handleExpand}
        >
          <img src={expand} alt="Expand" />
        </IconButton>
        <IconButton
          className="bg-[#fcfcfc] border border-[#dadee5] rounded-[8px] p-2 w-10 h-10"
          onClick={handleMapOptionsClick}
        >
          <img src={mapOptions} alt="Map Options" />
        </IconButton>
      </div>

      {/* Zoom controls */}
      <div className="absolute bottom-4 right-4 flex flex-col gap-2">
        <IconButton
          className="bg-[#fcfcfc] border border-[#dadee5] rounded-[8px] p-2 w-10 h-10"
          onClick={zoomIn}
        >
          <img src={plus} alt="Zoom In" />
        </IconButton>
        <IconButton
          className="bg-[#fcfcfc] border border-[#dadee5] rounded-[8px] p-2 w-10 h-10"
          onClick={zoomOut}
        >
          <img src={minus} alt="Zoom Out" />
        </IconButton>
      </div>

      {hoveredVehicle && (
        <div
          onMouseEnter={() => handleMouseEnter(hoveredVehicle)}
          onMouseLeave={handleMouseLeave}
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg border border-[#f0eff8] p-3 w-[285px] shadow-lg"
        >
          <div className="flex flex-col gap-2.5">
            <div className="flex justify-between items-center">
              <div className="flex gap-1">
                <span className="text-[#767676] text-sm uppercase font-normal">Reg. No. :</span>
                <span className="text-[#1b1b1b] text-sm font-semibold">
                  {hoveredVehicle.vehicleRegistrationNo}
                </span>
              </div>
              <button
                className="flex items-center gap-1 bg-[#eeedfcff] rounded px-2 py-1"
                onClick={handleLiveClick}
              >
                <img src={live} alt="Live" className="w-4 h-4" />
                <span className="text-[#353247] text-xs font-bold">Live</span>
              </button>
            </div>
            <div className="truncate">
              <span className="text-[#767676] text-sm uppercase font-normal">LAST TIME :</span>
              <span className="text-[#1b1b1b] text-sm font-semibold">
                {formatDateTime(hoveredVehicle.lastUpdateTime)}
              </span>
            </div>
            <div className="flex gap-1">
              <span className="text-[#767676] text-sm uppercase font-normal">Driver :</span>
              <span className="text-[#1b1b1b] text-sm font-semibold">&lt;N/A&gt;</span>
            </div>
            <div className="flex gap-2 items-start">
              <img src={locationIcon} alt="Location" className="w-4 h-4 mt-1" />
              <span className="text-sm text-[#1b1b1b] leading-[21px]">
                {vehicleAddresses[hoveredVehicle.vehicleRegistrationNo] || 'Fetching address...'}
              </span>
            </div>
            <div className="flex justify-between items-center mt-1">
              <span className="text-xs text-[#1b1b1b] opacity-50">
                Last Update: {getTimeDifference(hoveredVehicle.lastUpdateTime)}
              </span>
              <div className="flex gap-2">
                <IconButton className="p-1 hover:bg-gray-100" onClick={handleDownload}>
                  <img src={download} alt="Download" className="w-4 h-4" />
                </IconButton>
                <IconButton className="p-1 hover:bg-gray-100" onClick={handlePhoneClick}>
                  <img src={phone} alt="Call" className="w-4 h-4" />
                </IconButton>
              </div>
            </div>
            <div className="truncate">
              <span className="text-[#767676] text-sm uppercase font-normal">LAST SPEED :</span>
              <span className="text-[#1b1b1b] text-sm font-semibold">{hoveredVehicle.gpsSpeed} km/h</span>
            </div>
          </div>
        </div>
      )}

      {liveFeedModalOpen && selectedVehicle && (
        <LiveFeedModal
          open={liveFeedModalOpen}
          onClose={() => {
            setLiveFeedModalOpen(false);
            setSelectedVehicle(null);
          }}
          trip={{
            ...selectedVehicle,
            vehicleId: convertRegistrationNoToVehicleId(selectedVehicle.vehicleRegistrationNo),
          }}
          vehicleId={mapData[0].deviceId}
          type="live"
          isDash={true}
        />
      )}

      {callingModalOpen && selectedVehicle && (
        <CallingModal
          open={callingModalOpen}
          onClose={() => {
            setCallingModalOpen(false);
            setSelectedVehicle(null);
          }}
          tripInfo={{ vehicleId: convertRegistrationNoToVehicleId(selectedVehicle.vehicleRegistrationNo) }}
        />
      )}
    </div>
  );
};

export default MapPreview;